/** What Fonctionnalités and Règles share: a read that can be retried, and its loading and failed states. */
import { useEffect, useReducer, useRef, useState } from "react";
import { ErrorNotice } from "../shell/ErrorNotice";

export type Load<T> =
  | { status: "loading"; data: T | null }
  | { status: "ready"; data: T }
  | { status: "failed"; data: T | null; error: unknown };

/**
 * Reads `load()` whenever `key` changes or `reload()` is called, keeping the
 * last answer on screen meanwhile. An answer for an older key is dropped. A
 * null `load` reads nothing.
 */
export function useLoad<T>(load: (() => Promise<T>) | null, key: string | null) {
  const [state, setState] = useState<Load<T>>({ status: "loading", data: null });
  const [reloads, reload] = useReducer((count: number) => count + 1, 0);
  const generation = useRef(0);
  const latest = useRef(load);
  useEffect(() => {
    latest.current = load;
  });
  useEffect(() => {
    const mine = ++generation.current;
    const run = latest.current;
    if (!run) return;
    setState((current) => ({ status: "loading", data: current.data }));
    run().then(
      (data) => {
        if (generation.current === mine) setState({ status: "ready", data });
      },
      (error: unknown) => {
        if (generation.current === mine) setState((current) => ({ status: "failed", data: current.data, error }));
      },
    );
  }, [key, reloads]);
  return { state, reload };
}

export function Loading({ text }: { text: string }) {
  return (
    <div className="empty" role="status">
      <span className="spinner" aria-hidden="true" />
      <span>{text}</span>
    </div>
  );
}

export function LoadFailed({
  message,
  retry,
  error,
  onRetry,
}: {
  message: string;
  retry: string;
  error: unknown;
  onRetry: () => void;
}) {
  return (
    <div className="audit-error">
      <p className="foot">{message}</p>
      <ErrorNotice error={error} />
      <div className="btns">
        <button className="btn" type="button" onClick={onRetry}>
          {retry}
        </button>
      </div>
    </div>
  );
}
